/**
 * UserDataService - Abstraction layer for user data storage
 * 
 * Current implementation: localStorage
 * Future migration: Firebase/Supabase/custom backend
 * 
 * This service provides a clean interface for storing user-specific data
 * that can easily be swapped for a cloud-based solution later.
 */

import { AccessRequest, User } from '../types/auth';
import { UserProfile } from '../types/progress';
import { localStorageService } from './database/LocalStorageService';

export interface UserSettings {
  tabWidth: number;
  tabInputMode: 'tab' | 'spaces';
  errorMode: 'strict' | 'pass' | 'hold';
  audioVolume: number;
  audioEnabled: boolean;
}

const DEFAULT_SETTINGS: UserSettings = {
  tabWidth: 4,
  tabInputMode: 'tab',
  errorMode: 'strict',
  audioVolume: 80,
  audioEnabled: true,
};

class UserDataServiceClass {
  /**
   * Ensure user profile exists in storage, create if missing
   * Called automatically when user logs in
   * 
   * Now saves to BOTH localStorage (for local access) AND Firestore (for security rules)
   */
  async ensureUserProfile(user: User): Promise<UserProfile> {
    console.log('🔍 Ensuring user profile exists for:', user.uid);
    console.log('📸 Photo URL from auth:', user.photoURL);
    console.log('👤 Display name from auth:', user.displayName);
    console.log('🔐 Is Admin:', user.isAdmin);
    
    // Check if profile exists in localStorage
    let profile = await localStorageService.getUserProfile(user.uid);
    let needsAvatarSync = false;
    
    if (!profile) {
      console.log('📝 Creating new user profile...');
      // Create new profile (without SSO photoURL - users must choose custom avatar)
      profile = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || 'User',
        photoURL: '', // Don't use SSO photo - users choose custom avatar
        selectedKeyboard: '',
        selectedLanguage: '',
        createdAt: new Date(),
        lastLoginAt: new Date(),
      };
      await localStorageService.createUserProfile(profile);
      console.log('✅ User profile created in localStorage');
      needsAvatarSync = true; // New profile needs to sync avatar from Firestore
    } else {
      console.log('✅ User profile found in localStorage, updating...');
      // Update last login (don't overwrite with SSO photo)
      await localStorageService.updateUserProfile(user.uid, {
        lastLoginAt: new Date(),
        displayName: user.displayName || profile.displayName,
        // Don't update photoURL from SSO - preserve custom avatar choice
      });
      console.log('✅ Profile updated in localStorage');
      
      // Check if localStorage profile is missing avatar data
      if (!profile.customAvatarType && !profile.customAvatarValue) {
        console.log('⚠️ localStorage profile missing avatar data, will sync from Firestore');
        needsAvatarSync = true;
      }
    }
    
    // ALSO save to Firestore (for security rules to check isAdmin)
    try {
      const { firestoreService } = await import('./database/FirestoreService');
      
      // Check if profile exists in Firestore
      const firestoreProfile = await firestoreService.getUserProfile(user.uid);
      
      if (!firestoreProfile) {
        console.log('☁️ Creating user profile in Firestore with isAdmin:', user.isAdmin);
        await firestoreService.createUserProfile({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || 'User',
          photoURL: user.photoURL || '',
          isAdmin: user.isAdmin || false, // CRITICAL: Save isAdmin flag for security rules
          selectedKeyboard: '',
          selectedLanguage: '',
          createdAt: new Date(),
          lastLoginAt: new Date(),
        });
        console.log('✅ User profile created in Firestore');
        
        // If creating profile but isAdmin is false, warn the user
        if (!user.isAdmin) {
          console.warn('⚠️ Created Firestore profile without admin privileges');
          console.warn('   If you should be an admin, you need to:');
          console.warn('   1. Go to Firebase Console → Firestore Database');
          console.warn('   2. Navigate to users/' + user.uid);
          console.warn('   3. Add field: isAdmin (boolean) = true');
          console.warn('   4. Refresh the page');
        }
      } else {
        console.log('☁️ Updating user profile in Firestore...');
        
        // Build update object - preserve existing isAdmin flag from Firestore
        const updates: any = {
          lastLoginAt: new Date(),
          displayName: user.displayName || firestoreProfile.displayName,
          // Don't update photoURL from SSO - preserve custom avatar choice
        };
        
        // CRITICAL: Only update isAdmin if Firestore profile doesn't have it yet
        // This prevents accidentally removing admin status
        if (typeof firestoreProfile.isAdmin !== 'boolean') {
          console.log('  📝 Adding isAdmin flag to existing profile');
          updates.isAdmin = user.isAdmin || false;
        } else {
          console.log('  ✅ Preserving existing isAdmin flag:', firestoreProfile.isAdmin);
          // Do NOT include isAdmin in updates - keep what's in Firestore
        }
        
        await firestoreService.updateUserProfile(user.uid, updates);
        console.log('✅ User profile updated in Firestore');
        
        // SYNC AVATAR DATA: If localStorage is missing avatar but Firestore has it, sync down
        if (needsAvatarSync && firestoreProfile.customAvatarType) {
          console.log('⬇️ Syncing avatar data from Firestore to localStorage...');
          const avatarUpdates: Partial<UserProfile> = {};
          
          if (firestoreProfile.customAvatarType !== undefined) {
            avatarUpdates.customAvatarType = firestoreProfile.customAvatarType;
          }
          if (firestoreProfile.customAvatarValue !== undefined) {
            avatarUpdates.customAvatarValue = firestoreProfile.customAvatarValue;
          }
          if (firestoreProfile.avatarBackgroundType !== undefined) {
            avatarUpdates.avatarBackgroundType = firestoreProfile.avatarBackgroundType;
          }
          if (firestoreProfile.avatarBackgroundValue !== undefined) {
            avatarUpdates.avatarBackgroundValue = firestoreProfile.avatarBackgroundValue;
          }
          if (firestoreProfile.photoURL !== undefined) {
            avatarUpdates.photoURL = firestoreProfile.photoURL;
          }
          
          if (Object.keys(avatarUpdates).length > 0) {
            await localStorageService.updateUserProfile(user.uid, avatarUpdates);
            // Update the profile object to return the synced data
            profile = { ...profile, ...avatarUpdates };
            console.log('✅ Avatar data synced from Firestore:', avatarUpdates);
            
            // Dispatch event to notify components to refresh avatar display
            window.dispatchEvent(new CustomEvent('avatarChanged', {
              detail: { userId: user.uid }
            }));
          }
        }
      }
    } catch (error) {
      console.error('⚠️ Error syncing profile to Firestore (non-critical):', error);
      // Don't throw - localStorage profile is enough for app to work
    }
    
    return profile;
  }
  
  /**
   * Load user settings from Firestore (with localStorage cache)
   * Returns merged settings (admin defaults + user overrides)
   */
  async getUserSettings(userId: string): Promise<UserSettings> {
    try {
      // Try Firestore first
      const { firestoreService } = await import('./database/FirestoreService');
      const profile = await firestoreService.getUserProfile(userId);
      
      if (profile && (profile as any).settings) {
        console.log('☁️ Loaded user settings from Firestore:', (profile as any).settings);
        const settings = { ...DEFAULT_SETTINGS, ...(profile as any).settings };
        
        // Update localStorage cache
        localStorage.setItem('typingSettings', JSON.stringify(settings));
        
        return settings;
      }
    } catch (error) {
      console.warn('⚠️ Failed to load settings from Firestore, using cache:', error);
    }
    
    // Fallback to localStorage cache
    try {
      const saved = localStorage.getItem('typingSettings');
      if (saved) {
        const parsed = JSON.parse(saved);
        console.log('📦 Loaded user settings from cache:', parsed);
        return { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch (error) {
      console.error('Error loading user settings from cache:', error);
    }
    
    console.log('⚙️ Using default settings');
    return DEFAULT_SETTINGS;
  }
  
  /**
   * Save user settings to Firestore (with localStorage cache)
   */
  async saveUserSettings(userId: string, settings: UserSettings): Promise<void> {
    try {
      // Save to Firestore first
      const { firestoreService } = await import('./database/FirestoreService');
      await firestoreService.updateUserProfile(userId, {
        settings: settings as any
      });
      
      // Update localStorage cache
      localStorage.setItem('typingSettings', JSON.stringify(settings));
      
      console.log('✅ User settings saved to Firestore and cache:', settings);
    } catch (error) {
      console.error('❌ Failed to save settings to Firestore:', error);
      
      // Fallback: save to localStorage only
      try {
        localStorage.setItem('typingSettings', JSON.stringify(settings));
        console.log('💾 User settings saved to cache only:', settings);
      } catch (cacheError) {
        console.error('Error saving user settings to cache:', cacheError);
        throw cacheError;
      }
      
      throw error;
    }
  }
  
  /**
   * Get user profile (from localStorage)
   */
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    return localStorageService.getUserProfile(userId);
  }
  
  /**
   * Get isAdmin flag from Firestore (authoritative source)
   */
  async getIsAdminFromFirestore(userId: string): Promise<boolean> {
    try {
      const { firestoreService } = await import('./database/FirestoreService');
      const firestoreProfile = await firestoreService.getUserProfile(userId);
      
      if (!firestoreProfile) {
        console.warn('⚠️ No Firestore profile found for user:', userId);
        console.warn('   If you are an admin, you need to manually add isAdmin: true to your Firestore user document');
        return false;
      }
      
      if (typeof firestoreProfile.isAdmin === 'boolean') {
        console.log('☁️ Loaded isAdmin from Firestore:', firestoreProfile.isAdmin);
        return firestoreProfile.isAdmin;
      }
      
      console.log('⚠️ No isAdmin flag in Firestore profile, defaulting to false');
      console.log('   User document exists but missing isAdmin field');
      return false;
    } catch (error: any) {
      if (error?.code === 'permission-denied') {
        console.error('❌ Permission denied reading user profile from Firestore');
        console.error('   This might mean:');
        console.error('   1. Firestore rules are not deployed');
        console.error('   2. You are not authenticated');
        console.error('   3. The user document does not exist');
      } else {
        console.error('❌ Error loading isAdmin from Firestore:', error);
      }
      return false;
    }
  }
  
  /**
   * Update user profile
   * Now syncs to BOTH localStorage and Firestore
   */
  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<void> {
    // Update localStorage
    await localStorageService.updateUserProfile(userId, updates);

    // Also update Firestore (for cloud persistence)
    try {
      const { firestoreService } = await import('./database/FirestoreService');
      await firestoreService.updateUserProfile(userId, updates);
    } catch (error) {
      console.error('⚠️ Error syncing profile update to Firestore:', error);
      // Don't throw - localStorage update is enough for app to work
    }
  }

  /**
   * Public-deployment access gate. Reads /allowedDomains/{domain}.
   */
  async isDomainAllowed(email: string | null): Promise<boolean> {
    if (!email) return false;
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return false;
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      const snap = await getDoc(doc(db, 'allowedDomains', domain));
      return snap.exists();
    } catch (error) {
      console.error('Error checking allowedDomains:', error);
      return false;
    }
  }

  /**
   * Public-deployment access gate. Reads /allowlist/{emailLower}.
   */
  async isEmailAllowlisted(email: string | null): Promise<boolean> {
    if (!email) return false;
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      const snap = await getDoc(doc(db, 'allowlist', email.toLowerCase()));
      return snap.exists();
    } catch (error) {
      console.error('Error checking allowlist:', error);
      return false;
    }
  }

  /**
   * Read this user's pending/approved/denied request, or null if no request exists.
   */
  async getAccessRequestStatus(email: string | null): Promise<AccessRequest['status'] | null> {
    if (!email) return null;
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      const snap = await getDoc(doc(db, 'accessRequests', email.toLowerCase()));
      if (!snap.exists()) return null;
      const data = snap.data() as AccessRequest;
      return data.status ?? null;
    } catch (error) {
      console.error('Error reading accessRequest:', error);
      return null;
    }
  }

  /**
   * Submit a new access request for the signed-in user. Doc ID = lowercased email.
   * Firestore rules require: status='pending', email matches doc id, no domain access.
   */
  async submitAccessRequest(user: User): Promise<void> {
    if (!user.email) throw new Error('Cannot submit access request without an email');
    const { doc, setDoc } = await import('firebase/firestore');
    const { db } = await import('../config/firebase');
    const emailLower = user.email.toLowerCase();
    const request: AccessRequest = {
      email: emailLower,
      displayName: user.displayName,
      photoURL: user.photoURL,
      requestedAt: Date.now(),
      status: 'pending',
    };
    await setDoc(doc(db, 'accessRequests', emailLower), request);
  }
}

// Export singleton instance
export const userDataService = new UserDataServiceClass();

/**
 * MIGRATION PATH:
 * 
 * To migrate to cloud storage:
 * 1. Create new implementation (e.g., FirebaseUserDataService)
 * 2. Implement the same interface
 * 3. Add migration utility to sync local → cloud
 * 4. Update singleton to use new implementation
 * 
 * Example:
 * 
 * class FirebaseUserDataService implements IUserDataService {
 *   async ensureUserProfile(user: User): Promise<UserProfile> {
 *     // Check Firestore
 *     // Create if missing
 *   }
 *   
 *   async getUserSettings(userId: string): Promise<UserSettings> {
 *     // Read from Firestore
 *   }
 *   
 *   async saveUserSettings(userId: string, settings: UserSettings): Promise<void> {
 *     // Save to Firestore
 *   }
 * }
 * 
 * export const userDataService = new FirebaseUserDataService();
 */

